// Client-side permission mirror for the AUDIT TRAIL area (spec 16 §2.16/§5).
//
// The server is the ONLY authorisation boundary. This hook exists so the office
// nav does not offer the trail to the eight roles that cannot open it — and,
// just as importantly, so the screen can say WHICH of the two refusals it got.
//
// TWO GATES, AND THE CLIENT CAN ONLY SEE ONE OF THEM:
//
//   1. `identity.view_auth_events` — read here off the permission list
//      `GET /v1/auth/tenants` carries (D-586).
//   2. GROUP SCOPE — NOT derivable. `/v1/auth/tenants` returns `{subdomain,
//      role_codes}` and no scope, so a facility-scoped `facility_admin` looks
//      identical to a group-scoped one from here. The nav therefore offers the
//      trail to both, and the screen renders the server's own
//      `AUDIT_GROUP_SCOPE_REQUIRED` refusal with its own sentence rather than a
//      generic "not permitted" — a facility_admin who holds the code and is
//      told "you lack identity.view_auth_events" would go chasing a permission
//      they already have.
//
// Widening `/v1/auth/tenants` with `scope_kind` would let the nav hide the entry
// instead, and is the right fix; it is module 16's contract to change, not this
// area's, and hiding a link is a convenience while the refusal above is correct
// either way.

import {
  derivePermissions,
  rolesAreKnown,
  roleSignalOf,
  useRoleCodesFeed,
  type AreaPermissions,
  type RoleInput,
  type RoleSignal,
} from "../../../../lib/role-codes";

/** The gate this area reads. */
export const AUDIT_READ_PERMISSION = "identity.view_auth_events";

/**
 * Role code → `identity.*` codes: the identity slice of the contracts
 * `systemRoleGrants` table, transcribed cell by cell and bound to it by
 * `tools/ci/web-permission-mirror.ts` (a mirror that grants too LITTLE hides a
 * capability a role genuinely holds, and nobody files a bug about a link they
 * have never seen).
 *
 * The row that matters here: `identity.view_auth_events` is held by
 * `group_admin` and `facility_admin` and by NO other system role — the other
 * eight hold `identity.view_org`, and all but the floor worker hold
 * `identity.view_member_names` (D-442, the names read), and nothing else in
 * this namespace. A factory's
 * audit trail is an owner/admin surface, and that is what the catalog already
 * said before this area existed.
 */
export const IDENTITY_ROLE_GRANTS: Readonly<Record<string, readonly string[]>> = {
  group_admin: [
    "identity.invite_member",
    "identity.manage_org",
    "identity.manage_roles",
    "identity.manage_security_policy",
    // Saved-view moderation (spec 16 §7, D-305 ruling 15): group_admin alone
    // by default, and counted by the server only from a group-scoped grant.
    "identity.manage_shared_views",
    "identity.manage_work_centers",
    "identity.provision_member",
    "identity.revoke_member",
    "identity.revoke_session_any",
    "identity.unlock_account",
    "identity.update_member",
    "identity.view_auth_events",
    "identity.view_member_names",
    "identity.view_members",
    "identity.view_org",
    "identity.view_roles",
    "identity.view_sessions_any",
  ],
  // Everything group_admin holds EXCEPT role management, the security policy
  // and saved-view moderation — group-level governance (spec 16 §7; D-305
  // ruling 14 for the third, since a shared view is tenant-wide).
  facility_admin: [
    "identity.invite_member",
    "identity.manage_org",
    "identity.manage_work_centers",
    "identity.provision_member",
    "identity.revoke_member",
    "identity.revoke_session_any",
    "identity.unlock_account",
    "identity.update_member",
    "identity.view_auth_events",
    "identity.view_member_names",
    "identity.view_members",
    "identity.view_org",
    "identity.view_roles",
    "identity.view_sessions_any",
  ],
  planner: ["identity.view_member_names", "identity.view_org"],
  sales: ["identity.view_member_names", "identity.view_org"],
  procurement: ["identity.view_member_names", "identity.view_org"],
  finance: ["identity.view_member_names", "identity.view_org"],
  qc: ["identity.view_member_names", "identity.view_org"],
  maintenance: ["identity.view_member_names", "identity.view_org"],
  storekeeper: ["identity.view_member_names", "identity.view_org"],
  floor_worker: ["identity.view_org"],
};

/** The effective `identity.*` set (pure, unit-tested): the feed's permission
 * list cut to this slice, or the mirror without one (D-586). */
export function deriveIdentityPermissions(roles: RoleInput): Set<string> {
  return derivePermissions(roles, IDENTITY_ROLE_GRANTS);
}

/**
 * Does this principal hold a role this mirror does not know about?
 *
 * `derivePermissions` knows the TEN SYSTEM ROLES and nothing else, so a tenant's
 * CUSTOM role (spec 16 §3.9) derives no codes at all. For most areas that is
 * harmless — it hides a button. Here it is the difference between the feature
 * working and not: the principal the payload rule exists for is precisely a
 * custom "Auditor" role holding `identity.view_auth_events` without the cost
 * codes, and the API serves it. Treating an unrecognised role as a refusal
 * would fabricate a "not your screen" for exactly that user and never ask the
 * server — turning "we cannot tell" into "you are not permitted", which the
 * paragraph below forbids.
 */
export function hasUnknownRole(roles: RoleInput): boolean {
  // Since D-586 the feed says what every role holds, custom roles included,
  // so nothing is unknown when it carries the list. The question survives
  // only for a feed row without one.
  return !rolesAreKnown(roles, IDENTITY_ROLE_GRANTS);
}

export interface AuditPermissions extends AreaPermissions, RoleSignal {
  readonly roleCodes: ReadonlySet<string>;
  /** True when the role feed FAILED, as opposed to "settled with no grants". */
  readonly isFeedError: boolean;
  /** May the office nav offer the trail at all? True for a known holder AND —
   * deliberately — for a principal carrying a role this mirror cannot resolve,
   * because a custom role may well hold the code and the server decides. */
  readonly canReadAudit: boolean;
  /** At least one role code is not one of the ten system roles. */
  readonly hasUnknownRole: boolean;
}

export function useAuditPermissions(): AuditPermissions {
  const feed = useRoleCodesFeed();
  const codes = deriveIdentityPermissions(feed);
  const unknown = hasUnknownRole(feed);
  return {
    isResolved: feed.isResolved,
    has: (code: string) => codes.has(code),
    ...roleSignalOf(feed),
    isFeedError: feed.isFeedError,
    // Offer the entry to a custom-role principal too: the cost of a link that
    // 403s is one refusal sentence; the cost of hiding it is a tenant who built
    // an auditor role and cannot find the screen it was built for.
    canReadAudit: codes.has(AUDIT_READ_PERMISSION) || unknown,
    hasUnknownRole: unknown,
  };
}

/**
 * Is this principal REFUSED the trail — a stable fact, not a failure?
 *
 * True only once the feed has settled AND answered DEFINITIVELY. In flight, on
 * a FAILED feed, or when the principal carries a role this mirror cannot
 * resolve, the answer is "let the request go and render the server's own
 * refusal": a read surface must never turn "we cannot tell" into "you are not
 * permitted". Used twice — to keep the query from firing at all, and to render
 * the refusal state instead of an error card whose Retry can never succeed.
 */
export function isAuditReadRefused(perms: AuditPermissions): boolean {
  return perms.isResolved && !perms.isFeedError && !perms.hasUnknownRole && !perms.canReadAudit;
}
